import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Slider,
  Button,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import HeatMap from "../HeatMap";
import { useWebServer } from "../../../../../../providers";
import DevicesTable from "..//DevicesTable";
import CloseIcon from "@mui/icons-material/Close";
import ResponsiveButton from "../../../../../Forms/ResponsiveButton";
import { SLIDER_MARKS } from "../../../../../../utils/const";
import { modalStyle, heatmapContainerStyle, closeBtnStyle } from './style'


const AlgorithmSettingLayout = ({
  initialVectorType,
  width = 1000,
  height = 1000,
  updateAlgConfidenceSettings,
  latestAlgVersion,
  uniqueVectorTypes,
  devicesVectorTypes,
  onClose,
  algorithmConfidence,
  vectorTypeOptions,
  defaultAlgorithmConfidenceThreshold
}) => {
  const { sendRequest } = useWebServer();
  const [data, setData] = useState([]);
  const [percMarks, setPercMarks] = useState([]);
  const [specColumn, setSpecColumn] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [algConfidence, setAlgConfidence] = useState(
    uniqueVectorTypes?.[0]?.[0] === "m"
      ? algorithmConfidence?.["mosquito"]
      : algorithmConfidence?.["tick"]
  );
  const [lastFetchAlgConfidence, setLastFetchAlgConfidence] = useState(-2);
  const [previousSave, setPreviousSave] = useState(algConfidence);
  const [regions, setRegions] = useState([]);
  const [hideNonRegion, setHideNonRegion] = useState(true);
  const [isAlgConfidenceDirty, setIsAlgConfidenceDirty] = useState(false);
  const prevVectorType = useRef(null);
  const [vectorType, setVectorType] = useState(initialVectorType);

  useEffect(() => {
    if (prevVectorType?.current?.[0] !== vectorType?.[0]) {
      const updateConfidence = vectorType?.[0] === "m"
        ? algorithmConfidence?.["mosquito"]
        : algorithmConfidence?.["tick"]
      setAlgConfidence(updateConfidence);
      setPreviousSave(updateConfidence)
    }
    Promise.all([getData(-1), getSpecColumn()]).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vectorType, latestAlgVersion]);

  const saveAlgConfidence = async (algConfidence) => {
    await sendRequest("patch")({
      url: `/algorithm/algorithm-confidence/${vectorType?.[0] === "m" ? "mosquito" : "tick"
        }/${algConfidence}`,
      devPort: 50057,
    });
    updateAlgConfidenceSettings(vectorType?.[0] === "m" ? "mosquito" : "tick", algConfidence);
    setPreviousSave(algConfidence);
    setIsAlgConfidenceDirty(false);
  };

  const resetAlgConfidence = () => {
    setAlgConfidence(previousSave);
    setIsAlgConfidenceDirty(false);
  };

  const defaultAlgConfidence = () => {
    const conf =
      (vectorType?.startsWith("mos")
        ? defaultAlgorithmConfidenceThreshold.mosquito
        : vectorType?.startsWith("tick")
          ? defaultAlgorithmConfidenceThreshold.tick
          : defaultAlgorithmConfidenceThreshold.default);
    saveAlgConfidence(conf).then();
    setAlgConfidence(conf);
  };

  const onChangeAlgConfidence = (e) => {
    let num = e.target.value;
    if (e.target.value !== algConfidence) {
      let res = percMarks.reduce(
        (nl, curr) => (curr.value <= num && curr.value > nl ? curr.value : nl),
        0
      );
      if (res !== lastFetchAlgConfidence) {
        getData(res).then();
        setLastFetchAlgConfidence(res);
      }
    }
    setAlgConfidence(num);
  };

  const getSpecColumn = async () => {
    if (!vectorType && !latestAlgVersion) return;
    try {
      let res = await sendRequest("get")({
        url: `/algorithm/species/${vectorType}/${latestAlgVersion}`,
        devPort: 50057,
      });
      let regionSpp = []
      if (res?.data || {}) {

        const invisibleCharacters = [
          '\u200B', // Zero Width Space
          '\u200C', // Zero Width Non-Joiner
          '\u200D', // Zero Width Joiner
          '\uFEFF', // Zero Width No-Break Space
        ];
        let invisibleCharactersIndex = 0
        //region species name overwrite on classes
        let specName = [];
        for (let cur in res.data?.classes) {
          if (res.data?.region && cur in res.data?.region && res.data?.region?.[cur]) {
            specName.push({ id: cur, name: `${res.data?.region?.[cur]}${invisibleCharacters[invisibleCharactersIndex]}` });
            regionSpp.push(`${res.data?.region?.[cur]}${invisibleCharacters[invisibleCharactersIndex]}`)
          } else {
            specName.push({ id: cur, name: `${res.data?.classes?.[cur]}${invisibleCharacters[invisibleCharactersIndex]}` });
          }
          invisibleCharactersIndex = (invisibleCharactersIndex + 1) % 4
        }
        if (res?.data?.region) {
          setRegions([...regionSpp, "unknown"]);
        } else {
          setRegions([]);
        }
        setSpecColumn([
          ...specName
            .sort((a, b) => {
              return a?.id - b?.id;
            })
            .map((cur) => cur.name),
          "unknown",
        ]);
      }
    } catch (err) {
      setVectorType(vectorType.startsWith('m') ? 'mosquito' : 'tick')
    }
  };

  const getData = async (targetPerc) => {
    if (!vectorType && !latestAlgVersion) return;

    let algType = vectorType?.startsWith("mo") ? "mosquito" : "tick";
    let res = await sendRequest("get")({
      url: `/algorithm/confusionMatrix/${algType}/${latestAlgVersion}/${targetPerc}`,
      devPort: 50057,
    });
    const { normalizedMatrix, numericArray } = res.data;
    if (normalizedMatrix) {
      setMatrix(normalizedMatrix);
      setPercMarks(
        numericArray.map((d) => {
          return { value: Number.parseInt(d), label: d };
        })
      );
    }
  };

  useEffect(() => {
    if (specColumn.length === 0) return;
    let processedData = [];
    matrix.forEach((d, x) => {
      d.forEach((cur, y) => {
        processedData.push({
          y: specColumn[x],
          x: specColumn[y],
          value: Number.parseFloat(cur),
        });
      });
    });
    setData(processedData);
  }, [specColumn, matrix]);

  return (
    <Box
      style={modalStyle}
      className={" bg-white rounded-xl border-4 border-transparent"}
    >
      <ResponsiveButton
        variant="contained"
        style={closeBtnStyle}
        onClick={onClose}
      >
        <CloseIcon
          sx={{
            fontSize: ["1.5rem", "1.5rem", "1.5rem", "1.5rem", "2rem", "2rem"],
          }}
        />
      </ResponsiveButton>
      <Box style={heatmapContainerStyle}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: ["1rem", "1rem", "1rem", "1rem", "1.5rem", "1.5rem"],
            mb: "0.5rem",
            mt: "1rem",
          }}
        >
          Algorithm Settings
        </Typography>

        <Box sx={{ minWidth: 120, display: "inline-block" }}>
          <FormControl size="small">
            <InputLabel id="demo-simple-select-label">Algorithm</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={vectorType}
              sx={{ minWidth: "10rem" }}
              label="Algorithm"
              onChange={(e) => setVectorType(e.target.value)}
            >
              {vectorTypeOptions.map((cur) => (
                <MenuItem value={cur?.value} key={cur.value}>{cur?.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography
            sx={{
              height: "40px",
              lineHeight: "40px",
              display: "inline-block",
              fontWeight: 300,
              fontSize: ["1rem", "1rem", "1rem", "1rem", "1.5rem", "1.5rem"],
            }}
          >
            {" "}
            --- {latestAlgVersion}
          </Typography>
        </Box>
        <Box sx={{ width: 700, mt: "2.5rem" }}>
          <Typography
            sx={{ display: "inline-block", fontWeight: 300, float: "left" }}
          >
            Algorithm Confidence Threshold:
          </Typography>
          {isAlgConfidenceDirty ? (
            <>
              <Button
                sx={{ display: "inline-block", float: "right" }}
                size="medium"
                variant="outlined"
                onClick={(e) => saveAlgConfidence(algConfidence)}
              >
                Save
              </Button>
              <Button
                sx={{ display: "inline-block", float: "right", mr: ".5rem" }}
                size="medium"
                variant="contained"
                style={{ backgroundColor: "#E10050" }}
                onClick={(e) => resetAlgConfidence()}
              >
                Restore
              </Button>
              <Button
                sx={{ display: "inline-block", float: "right", mr: ".5rem" }}
                size="medium"
                variant="outlined"
                onClick={(e) => defaultAlgConfidence()}
              >
                {`Default conf(${parseInt(
                  (vectorType?.startsWith("mos")
                    ? defaultAlgorithmConfidenceThreshold.mosquito
                    : vectorType?.startsWith("tick")
                      ? defaultAlgorithmConfidenceThreshold.tick
                      : defaultAlgorithmConfidenceThreshold.mosquito)
                )})`}
              </Button>
            </>
          ) : null}
        </Box>
        {percMarks && percMarks.length > 0 && (
          <Box sx={{ width: 700 }}>
            <Slider
              valueLabelDisplay="auto"
              value={algConfidence}
              step={1}
              marks={SLIDER_MARKS}
              onChange={(e) => onChangeAlgConfidence(e)}
              onChangeCommitted={(e) => {
                setIsAlgConfidenceDirty(previousSave !== algConfidence);
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            width: 700,
            display: "flex",
            justifyContent: "right",
            alignItems: "center",
          }}
        >
          {regions?.length > 1 && (
            <Button
              sx={{ display: "inline-block", float: "right" }}
              size="medium"
              variant="outlined"
              onClick={() => setHideNonRegion(!hideNonRegion)}
            >
              {!hideNonRegion ? "Hide" : "Show all"}
            </Button>
          )}
        </Box>

        <div style={{ position: "relative" }}>
          <HeatMap
            data={data}
            height={height}
            width={width}
            regions={regions}
            hideNonRegion={hideNonRegion}
          ></HeatMap>
        </div>
        <DevicesTable devicesVectorTypes={devicesVectorTypes} />
      </Box>
    </Box>
  );
};

export default AlgorithmSettingLayout;
